import { clearPendingPromo } from '../config/promos';

/**
 * getSmartReturnTo — computes the post-login return destination.
 *
 * Preserves the current URL (including search params like ?gid=...&mkt=...&src=tw)
 * so featured picks remain highlighted after the user logs in.
 *
 * Falls back to '/edgeboard' if currently on the root path, since redirecting back
 * to '/' after login would just show the landing page again.
 */
export const getSmartReturnTo = (src) => {
  const path = window.location.pathname === '/' ? '/edgeboard' : window.location.pathname;
  const params = new URLSearchParams(window.location.search);
  if (src) params.set('src', src);
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
};

/**
 * Flags that the next /edgeboard load should open the 7-day trial checkout.
 * Dashboard reads and clears it, and opens the dialog in trial mode for anyone
 * without full access -- no promo param can turn the trial off on this path.
 */
export const setTrialIntent = () => {
  try { localStorage.setItem('trialIntent', 'true'); } catch {}
  // A trial request beats an earlier saved promo code (config/promos.js:
  // "the later of the two wins"), on every path that sets it.
  clearPendingPromo();
};

/**
 * Sets trial intent flag and redirects to Auth0 signup. `returnTo` overrides
 * the smart default for doors whose own path cannot open checkout: only
 * /edgeboard reads trialIntent and opens the trial dialog.
 */
export const startTrial = (loginWithRedirect, src = 'eb', returnTo = null) => {
  setTrialIntent();
  loginWithRedirect({
    authorizationParams: { screen_hint: 'signup' },
    appState: { returnTo: returnTo || getSmartReturnTo(src) }
  });
};

const KNOWN_USERS_KEY = 'pev_known_users';

/**
 * classifyAuthEvent — distinguishes a first-time 'signup' from a returning 'login'.
 *
 * Auth0 doesn't tell us client-side whether a returning user is new, so we record
 * each user id we've seen on this device. First sighting => 'signup', thereafter
 * 'login'. Falls back to 'login' if storage is unavailable (never reports a false
 * signup, which would inflate the top of the funnel). Returns null when there's no
 * id to classify.
 *
 * Note: this is per-device. For cross-device accuracy, add an Auth0 post-login
 * Action that stamps an `is_new` claim and prefer that signal when present.
 */
export const classifyAuthEvent = (userSub, storage) => {
  if (!userSub) return null;
  const store = storage || (typeof window !== 'undefined' ? window.localStorage : null);
  try {
    if (!store) return 'login';
    const raw = store.getItem(KNOWN_USERS_KEY);
    const known = raw ? JSON.parse(raw) : [];
    if (known.includes(userSub)) return 'login';
    known.push(userSub);
    store.setItem(KNOWN_USERS_KEY, JSON.stringify(known));
    return 'signup';
  } catch (e) {
    return 'login';
  }
};
